import React, { useMemo, useState } from 'react';
import { TeamMemberLanes } from '../components/TeamMemberLanes';
import { HeatMapGrid } from '../components/HeatMapGrid';
import { MetricsOverview } from '../components/MetricsOverview';
import { TeamMemberFilter } from '../components/TeamMemberFilter';
import { ViewControls, ViewDensity, ViewMode } from '../components/ViewControls';
import { LoanDetailModal } from '../components/LoanDetailModal';
import { RecordSelectorModal } from '../components/RecordSelectorModal';
import { TEAM_MEMBERS, MOCK_DEALS } from '../data/mockPipelineData';
import { PipelineMetrics, Deal, Stage } from '../types/pipeline';
import { LayoutDashboard, Bell, Search, Settings } from 'lucide-react';
export function PipelineDashboard() {
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [viewMode, setViewMode] = useState<ViewMode>('lanes');
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(new Set());
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [pendingSelectionDeals, setPendingSelectionDeals] = useState<{
    deals: Deal[];
    stage: string;
  } | null>(null);
  const [heatMapMetric, setHeatMapMetric] = useState<'count' | 'value'>('count');
  const filteredDeals = useMemo(() => {
    let deals = MOCK_DEALS;
    if (selectedMemberId) {
      deals = deals.filter(d => d.assignedTo === selectedMemberId);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      deals = deals.filter(d => d.borrowerName.toLowerCase().includes(query) || d.loanType.toLowerCase().includes(query) || d.propertyType.toLowerCase().includes(query));
    }
    return deals;
  }, [selectedMemberId, searchQuery]);
  const visibleMembers = useMemo(() => {
    if (!selectedMemberId) return TEAM_MEMBERS;
    return TEAM_MEMBERS.filter(m => m.id === selectedMemberId);
  }, [selectedMemberId]);
  const metrics: PipelineMetrics = useMemo(() => {
    const totalValue = filteredDeals.reduce((sum, d) => sum + d.amount, 0);
    const totalDeals = filteredDeals.length;
    const avgDealSize = totalDeals > 0 ? totalValue / totalDeals : 0;
    const avgDays = totalDeals > 0 ? filteredDeals.reduce((sum, d) => sum + d.daysInStage, 0) / totalDeals : 0;
    const conversionRate = 68;
    return {
      totalValue,
      totalDeals,
      avgDealSize,
      avgDaysInPipeline: avgDays,
      conversionRate
    };
  }, [filteredDeals]);
  const toggleLane = (memberId: string) => {
    setCollapsedLanes(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };
  const toggleAllLanes = () => {
    if (collapsedLanes.size === visibleMembers.length) {
      setCollapsedLanes(new Set());
    } else {
      setCollapsedLanes(new Set(visibleMembers.map(m => m.id)));
    }
  };
  const handleHeatMapCellClick = (memberId: string, stage: Stage) => {
    const cellDeals = filteredDeals.filter(d => d.assignedTo === memberId && d.stage === stage);
    if (cellDeals.length === 1) {
      setSelectedDeal(cellDeals[0]);
    } else if (cellDeals.length > 1) {
      setPendingSelectionDeals({
        deals: cellDeals,
        stage
      });
    }
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900" data-id="element-396">
      {/* Top Navigation */}

      {/* Main Content */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-397">
        {/* Header Section */}
        <div className="mb-8" data-id="element-398">
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-399">
            Team Overview
          </h2>
          <p className="text-slate-500 text-base" data-id="element-400">
            Track pipeline velocity and team performance in real-time.
          </p>
        </div>

        {/* Metrics Cards */}
        <MetricsOverview metrics={metrics} data-id="element-401" />

        {/* Filter Bar */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-402">
          <TeamMemberFilter members={TEAM_MEMBERS} selectedMemberId={selectedMemberId} onSelectMember={setSelectedMemberId} data-id="element-403" />

          <div className="flex items-center justify-between lg:justify-end space-x-4" data-id="element-404">
            <ViewControls density={viewDensity} onDensityChange={setViewDensity} allExpanded={collapsedLanes.size === 0} onToggleAll={toggleAllLanes} viewMode={viewMode} onViewModeChange={setViewMode} data-id="element-405" />

            <div className="flex items-center space-x-2 text-sm text-slate-500" data-id="element-406">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-407"></span>
              <span className="hidden sm:inline font-medium" data-id="element-408">Live Updates</span>
            </div>
          </div>
        </div>

        {/* Main Visualization */}
        <div className="mb-8" data-id="element-409">
          {viewMode === 'lanes' ? <TeamMemberLanes members={visibleMembers} deals={filteredDeals} density={viewDensity} collapsedLanes={collapsedLanes} onToggleLane={toggleLane} onDealClick={setSelectedDeal} data-id="element-410" /> : <HeatMapGrid members={visibleMembers} deals={filteredDeals} metric={heatMapMetric} onMetricChange={setHeatMapMetric} onCellClick={handleHeatMapCellClick} data-id="element-411" />}
        </div>
      </main>

      {/* Record Selector Modal */}
      {pendingSelectionDeals && !selectedDeal && <RecordSelectorModal deals={pendingSelectionDeals.deals} stage={pendingSelectionDeals.stage} onSelect={deal => {
      setSelectedDeal(deal);
      // Don't clear pendingSelectionDeals here to allow navigation back
    }} onClose={() => setPendingSelectionDeals(null)} data-id="element-412" />}

      {/* Loan Detail Modal */}
      {selectedDeal && <LoanDetailModal deal={selectedDeal} onClose={() => {
      setSelectedDeal(null);
      setPendingSelectionDeals(null); // Close everything
    }} onBack={pendingSelectionDeals ? () => setSelectedDeal(null) : undefined} data-id="element-413" />}
    </div>;
}